import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Warning, Shield, Coins, ArrowSquareOut, Copy, ArrowCounterClockwise,
  CheckCircle, XCircle, Trash, MagnifyingGlass, Clock
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import AdminNav from './AdminNav';
import { extractTxHash, isValidTxHash } from '../../lib/txHash';
import { sendUsdcRefund } from '../../lib/refundViaWallet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DISPUTE_STATUS = {
  pending:       { label: 'Pending',       color: '#F59E0B', icon: Clock },
  investigating: { label: 'Investigating', color: '#007AFF', icon: MagnifyingGlass },
  refunded:      { label: 'Refunded',      color: '#94A3B8', icon: ArrowCounterClockwise },
  released:      { label: 'Released',      color: '#10B981', icon: CheckCircle },
  canceled:      { label: 'Canceled',      color: '#64748B', icon: XCircle },
};

const explorerTx = (chain, hash) => !hash ? null :
  chain === 'base' ? `https://basescan.org/tx/${hash}` : `https://etherscan.io/tx/${hash}`;

const explorerWallet = (chain, addr) => !addr ? null :
  chain === 'base' ? `https://basescan.org/address/${addr}` : `https://etherscan.io/address/${addr}`;

const shortAddr = (a) => a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '';

const AdminDisputes = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({ open: 0, refunded: 0, released: 0 });
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);

  const [refundOrder, setRefundOrder] = useState(null);
  const [refundHash, setRefundHash] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  const [sendingRefund, setSendingRefund] = useState(false);
  const [deleteOrder, setDeleteOrder] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem('admin_token');
    if (!t) { navigate('/admin/login'); return; }
    load();
    // eslint-disable-next-line
  }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.get(`${API_URL}/api/admin/disputes?filter=${filter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(r.data.orders || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error('Failed to load disputes');
      if (e.response?.status === 401) navigate('/admin/login');
    } finally { setLoading(false); }
  };

  const copy = (label, text) => {
    navigator.clipboard?.writeText(text);
    toast.success(`Copied ${label}`);
  };

  const updateDisputeStatus = async (orderId, newStatus, notes = '') => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin/web3-orders/${orderId}/dispute-status`,
        { dispute_status: newStatus, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Dispute marked ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const issueRefund = async () => {
    if (!refundOrder) return;
    if (!refundHash || !refundHash.startsWith('0x') || refundHash.length !== 66) {
      toast.error('Refund tx hash must be 0x + 64 hex chars');
      return;
    }
    setResolving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const endpoint = refundOrder.escrow_state === 'disputed'
        ? `/api/admin/web3-orders/${refundOrder.id}/resolve-dispute`
        : `/api/admin/web3-orders/${refundOrder.id}/refund`;
      const body = refundOrder.escrow_state === 'disputed'
        ? { resolution: 'refund', refund_tx_hash: refundHash, notes: refundNotes }
        : { refund_tx_hash: refundHash, chain: refundOrder.chain, notes: refundNotes };
      await axios.post(`${API_URL}${endpoint}`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Refund recorded on-chain');
      setRefundOrder(null);
      setRefundHash('');
      setRefundNotes('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally { setResolving(false); }
  };

  const releaseDispute = async (orderId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API_URL}/api/admin/web3-orders/${orderId}/resolve-dispute`,
        { resolution: 'release', notes: 'Admin confirmed shipment; releasing escrow.' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Released — dispute resolved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Release failed');
    }
  };

  const cancelDispute = async (orderId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin/web3-orders/${orderId}/dispute-status`,
        { dispute_status: 'canceled', notes: 'Dispute voided by admin — returned to escrow held state.' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Dispute canceled — escrow back to held');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const confirmDelete = async () => {
    if (!deleteOrder) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API_URL}/api/admin/web3-orders/bulk-delete`,
        { ids: [deleteOrder.id] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deleted ${deleteOrder.id}`);
      setDeleteOrder(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <AdminNav title="Dispute Console" titleTestId="admin-disputes-title" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="disputes-page">
        {/* Counts + filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Tab active={filter === 'open'}     onClick={() => setFilter('open')}     label="Open disputes" count={counts.open}     color="#EF4444" testid="tab-open" />
          <Tab active={filter === 'resolved'} onClick={() => setFilter('resolved')} label="Resolved"       count={counts.refunded + counts.released} color="#10B981" testid="tab-resolved" />
          <Tab active={filter === 'all'}      onClick={() => setFilter('all')}      label="All"            count={counts.open + counts.refunded + counts.released} color="#007AFF" testid="tab-all" />
        </div>

        {loading ? (
          <p className="text-center text-[#94A3B8] py-12">Loading…</p>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 bg-[#11141D] border border-[#1E293B] rounded-lg">
            <Shield size={48} className="mx-auto text-[#10B981] mb-3" weight="fill" />
            <p className="text-[#94A3B8]">No disputes in this view. Nice.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="disputes-list">
            {orders.map(o => (
              <DisputeCard
                key={o.id}
                order={o}
                onCopy={copy}
                onChangeStatus={updateDisputeStatus}
                onOpenRefund={() => setRefundOrder(o)}
                onRelease={() => releaseDispute(o.id)}
                onCancelDispute={() => cancelDispute(o.id)}
                onDelete={() => setDeleteOrder(o)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Refund dialog */}
      <Dialog open={!!refundOrder} onOpenChange={(open) => !open && !resolving && setRefundOrder(null)}>
        <DialogContent className="bg-[#11141D] border-[#EF4444] text-white max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-[#EF4444] flex items-center gap-2">
              <ArrowCounterClockwise size={20} /> Record refund — {refundOrder?.id}
            </DialogTitle>
          </DialogHeader>
          {refundOrder && (() => {
            const hashValid = isValidTxHash(extractTxHash(refundHash));
            const doCopy = (label, text) => { navigator.clipboard?.writeText(text); toast.success(`${label} copied`); };
            return (
              <div className="space-y-4">
                {/* Amount banner */}
                <div className="bg-[#0B1120] border border-[#10B981]/40 rounded-lg p-4 text-center">
                  <p className="text-[10px] uppercase text-[#64748B] font-['JetBrains_Mono']">Send this exact amount</p>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <p className="text-3xl font-['JetBrains_Mono'] font-bold text-[#10B981]">
                      ${refundOrder.total_usd?.toFixed(2)}
                    </p>
                    <span className="text-sm text-[#94A3B8]">{refundOrder.payment_token || 'USDC'}</span>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-1">on <span className="font-bold">{refundOrder.chain?.toUpperCase()}</span> network</p>
                  <button onClick={() => doCopy('Amount', refundOrder.total_usd?.toFixed(2))}
                          className="mt-2 text-[10px] text-[#007AFF] hover:text-white inline-flex items-center gap-1 font-['JetBrains_Mono']"
                          data-testid="copy-refund-amount-disputes">
                    <Copy size={10} /> copy amount
                  </button>
                </div>

                {/* Destination wallet */}
                <div className="bg-[#0B1120] border border-[#334155] rounded-lg p-4">
                  <p className="text-[10px] uppercase text-[#64748B] font-['JetBrains_Mono'] mb-1">Send back to buyer wallet</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-cyan-300 text-xs break-all font-mono">{refundOrder.wallet_address}</code>
                    <button onClick={() => doCopy('Address', refundOrder.wallet_address)}
                            className="flex-shrink-0 px-2 py-1 rounded bg-[#007AFF] hover:bg-[#0047AB] text-white text-xs font-['JetBrains_Mono'] inline-flex items-center gap-1"
                            data-testid="copy-buyer-wallet-in-dialog">
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                </div>

                <ol className="text-xs text-[#94A3B8] space-y-1 list-decimal list-inside pl-1">
                  <li>Open your wallet on {refundOrder.chain?.toUpperCase()}, send <span className="text-[#10B981] font-bold">${refundOrder.total_usd?.toFixed(2)} {refundOrder.payment_token || 'USDC'}</span> to the address above</li>
                  <li>Wait for on-chain confirmation</li>
                  <li>Paste the tx hash OR full BaseScan/Etherscan URL below — we'll extract it</li>
                </ol>

                {/* One-tap refund from admin's connected wallet */}
                <div className="bg-[#0B1120] border border-[#F59E0B]/40 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-['JetBrains_Mono'] text-[#F59E0B]">⚡ One-tap refund</p>
                  <p className="text-[11px] text-[#94A3B8]">
                    Pops your connected wallet to send ${refundOrder.total_usd?.toFixed(2)} {refundOrder.payment_token || 'USDC'} on {refundOrder.chain?.toUpperCase()}. Auto-fills the hash.
                  </p>
                  <Button
                    onClick={async () => {
                      setSendingRefund(true);
                      try {
                        const { txHash } = await sendUsdcRefund({
                          to: refundOrder.wallet_address,
                          amountUsd: refundOrder.total_usd,
                          chain: refundOrder.chain,
                        });
                        setRefundHash(txHash);
                        toast.success('Refund sent — now click "Record refund" to flip status');
                      } catch (err) {
                        toast.error(err?.message || 'Wallet rejected or failed');
                      } finally { setSendingRefund(false); }
                    }}
                    disabled={sendingRefund}
                    className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-black font-['JetBrains_Mono'] font-bold disabled:opacity-50"
                    data-testid="send-refund-from-wallet-disputes">
                    {sendingRefund ? 'Sending…' : `Send $${refundOrder.total_usd?.toFixed(2)} ${refundOrder.payment_token || 'USDC'} now`}
                  </Button>
                </div>

                <div className="space-y-1.5">
                  <Input placeholder="Paste tx hash OR BaseScan/Etherscan URL"
                         value={refundHash}
                         onChange={(e) => setRefundHash(e.target.value)}
                         className={`bg-[#11141D] font-mono text-xs ${hashValid ? 'border-[#10B981]' : refundHash ? 'border-[#EF4444]' : 'border-[#334155]'}`}
                         data-testid="refund-hash-input" />
                  {refundHash && hashValid && (
                    <p className="text-[11px] text-[#10B981] font-['JetBrains_Mono'] flex items-center gap-1">
                      <CheckCircle size={12} weight="fill" /> Hash extracted: {extractTxHash(refundHash).slice(0, 10)}…{extractTxHash(refundHash).slice(-6)}
                    </p>
                  )}
                  {refundHash && !hashValid && (
                    <p className="text-[11px] text-[#EF4444] font-['JetBrains_Mono']">No valid 0x + 64-hex hash found in this input</p>
                  )}
                </div>

                <Textarea placeholder="Admin notes (visible in buyer's order history)"
                          value={refundNotes}
                          onChange={(e) => setRefundNotes(e.target.value)}
                          className="bg-transparent border-[#334155]" rows={2}
                          data-testid="refund-notes-input" />
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOrder(null)} disabled={resolving} className="border-[#334155]">Cancel</Button>
            <Button onClick={issueRefund} disabled={resolving || !isValidTxHash(extractTxHash(refundHash))}
                    className="bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-40"
                    data-testid="confirm-refund-disputes">
              {resolving ? 'Recording…' : 'Record refund → flip to Refunded'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteOrder} onOpenChange={(open) => !open && setDeleteOrder(null)}>
        <DialogContent className="bg-[#11141D] border-[#EF4444] text-white max-w-md" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-[#EF4444] flex items-center gap-2">
              <Trash size={20} /> Delete {deleteOrder?.id}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#94A3B8]">
            Permanently removes this order from the database. On-chain txs are unaffected.
            Use this for spam disputes or test records you want cleaned up.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOrder(null)} className="border-[#334155]">Cancel</Button>
            <Button onClick={confirmDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-delete-dispute">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Tab = ({ active, onClick, label, count, color, testid }) => (
  <button onClick={onClick} data-testid={testid}
          className={`px-4 py-2 rounded-lg font-['JetBrains_Mono'] text-sm transition ${active ? 'border-2' : 'border border-[#1E293B] text-[#94A3B8] hover:text-white'}`}
          style={active ? { borderColor: color, color, background: `${color}11` } : {}}>
    {label} <span className="ml-1 text-xs opacity-70">({count})</span>
  </button>
);

const DisputeCard = ({ order, onCopy, onChangeStatus, onOpenRefund, onRelease, onCancelDispute, onDelete }) => {
  const dstatus = order.dispute_status || (order.escrow_state === 'disputed' ? 'pending' : '—');
  const meta = DISPUTE_STATUS[dstatus] || { label: '—', color: '#64748B', icon: Shield };
  const MetaIcon = meta.icon;

  return (
    <div className="bg-[#11141D] border rounded-lg p-4 space-y-3"
         style={{ borderColor: order.escrow_state === 'disputed' ? '#EF4444' : `${meta.color}55` }}
         data-testid={`dispute-card-${order.id}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-mono font-bold text-cyan-400">{order.id}</p>
          <span className="px-2 py-0.5 rounded text-xs font-bold font-['JetBrains_Mono'] inline-flex items-center gap-1"
                style={{ background: `${meta.color}22`, color: meta.color }}>
            <MetaIcon size={11} weight="fill" /> {meta.label}
          </span>
          <span className="text-xs text-[#94A3B8]">{order.chain?.toUpperCase()} · {order.payment_token || 'USDC'}</span>
          <span className="text-[#10B981] text-sm font-bold">${order.total_usd?.toFixed(2)}</span>
        </div>
        <button onClick={onDelete}
                className="text-[#94A3B8] hover:text-[#EF4444] transition p-1"
                aria-label="Delete record"
                data-testid={`delete-${order.id}`}>
          <Trash size={16} />
        </button>
      </div>

      {/* Dispute reason (if any) */}
      {order.dispute_reason && (
        <div className="bg-[#05050A] border-l-2 border-[#EF4444] px-3 py-2">
          <p className="text-[10px] uppercase text-[#EF4444] font-['JetBrains_Mono']">Buyer's reason</p>
          <p className="text-sm text-[#CBD5E1] italic">"{order.dispute_reason}"</p>
          {order.disputed_at && (
            <p className="text-[10px] text-[#64748B] mt-1">Filed {new Date(order.disputed_at).toLocaleString()}</p>
          )}
        </div>
      )}

      {/* On-chain data quick access */}
      <div className="grid sm:grid-cols-2 gap-2 text-xs">
        <OnChainRow label="Payment tx" value={order.payment_tx_hash} onCopy={(v) => onCopy('tx', v)}
                    explorerUrl={explorerTx(order.chain, order.payment_tx_hash)} testid={`payment-tx-${order.id}`} />
        <OnChainRow label="Buyer wallet" value={order.wallet_address} onCopy={(v) => onCopy('wallet', v)}
                    explorerUrl={explorerWallet(order.chain, order.wallet_address)} testid={`buyer-wallet-${order.id}`} />
        {order.refund_tx_hash && (
          <OnChainRow label="Refund tx" value={order.refund_tx_hash} onCopy={(v) => onCopy('refund tx', v)}
                      explorerUrl={explorerTx(order.refund_chain || order.chain, order.refund_tx_hash)} testid={`refund-tx-${order.id}`} />
        )}
      </div>

      {/* Status dropdown */}
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[#1E293B]">
        <label className="text-xs text-[#94A3B8] font-['JetBrains_Mono']">Set dispute status:</label>
        <Select value={dstatus} onValueChange={(v) => onChangeStatus(order.id, v)}>
          <SelectTrigger className="w-44 bg-transparent border-[#334155] text-xs h-8" data-testid={`status-select-${order.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(DISPUTE_STATUS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Actions */}
      {order.escrow_state === 'disputed' && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-[#1E293B]">
          <Button onClick={onOpenRefund} size="sm" className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid={`refund-btn-${order.id}`}>
            <ArrowCounterClockwise size={14} className="mr-1" /> Refund to buyer
          </Button>
          <Button onClick={onRelease} size="sm" className="bg-[#10B981] hover:bg-[#059669]" data-testid={`release-btn-${order.id}`}>
            <CheckCircle size={14} className="mr-1" /> Ship confirmed — release
          </Button>
          <Button onClick={onCancelDispute} size="sm" variant="outline" className="border-[#64748B] text-[#94A3B8]" data-testid={`void-btn-${order.id}`}>
            <XCircle size={14} className="mr-1" /> Void dispute (back to held)
          </Button>
        </div>
      )}

      {order.escrow_state === 'refunded' && order.escrow_refunded_at && (
        <p className="text-xs text-[#94A3B8]">Refunded {new Date(order.escrow_refunded_at).toLocaleString()}</p>
      )}
      {order.escrow_state === 'released' && order.escrow_released_at && (
        <p className="text-xs text-[#10B981]">Released {new Date(order.escrow_released_at).toLocaleString()}</p>
      )}

      {/* Admin notes */}
      {(order.notes || order.dispute_admin_notes) && (
        <div className="bg-[#05050A] rounded p-2 text-xs text-[#94A3B8]">
          {order.dispute_admin_notes && <p>Admin note: {order.dispute_admin_notes}</p>}
          {order.notes && <p>Order note: {order.notes}</p>}
        </div>
      )}
    </div>
  );
};

const OnChainRow = ({ label, value, onCopy, explorerUrl, testid }) => (
  <div className="bg-[#05050A] rounded px-2 py-1.5 flex items-center justify-between gap-2" data-testid={testid}>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] uppercase text-[#64748B] font-['JetBrains_Mono']">{label}</p>
      <code className="text-cyan-300 text-[11px] font-mono truncate block">{shortAddr(value)}</code>
    </div>
    <div className="flex items-center gap-1 flex-shrink-0">
      <button onClick={() => onCopy(value)} className="text-[#94A3B8] hover:text-white p-1" aria-label="Copy">
        <Copy size={12} />
      </button>
      {explorerUrl && (
        <a href={explorerUrl} target="_blank" rel="noopener noreferrer"
           className="text-[#007AFF] hover:text-white p-1" aria-label="Open in explorer">
          <ArrowSquareOut size={12} />
        </a>
      )}
    </div>
  </div>
);

export default AdminDisputes;
